import React, { useEffect, useState } from 'react';
import { Button, Checkbox, FormControl, InputLabel, ListItemText, MenuItem, Select } from '@mui/material';
import { LegacyGrid as Grid } from '../../elements';
import { ChartHelpers } from './chart-helpers';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { AnimalActivity } from './types';
import ReactApexChart from 'react-apexcharts';
import ApexCharts, { ApexOptions } from 'apexcharts';
import './styles.css';
import { VitalityTrackerService } from '../../hooks/VitalityTrackerService';
import LocationsService from '../../hooks/LocationsService';
import { AnimalGroupAnalyticDetails, FloorPlanAreaType } from '../../models/vitality-tracker';
import { tokens } from '../../theme/tokens';

type GraphSeries = {
  x: string;
  y: number[];
};

type GraphData = {
  name: string;
  data: GraphSeries[];
};

class DetailedProps {
  locations: { id: string; name: string }[];
  activities: AnimalActivity[];

  constructor() {
    this.activities = [];
    this.locations = [];
  }
}

const Detailed: React.FC<DetailedProps> = ({ activities, locations }) => {
  const vitalityTrackerService = new VitalityTrackerService();
  const locationsService = new LocationsService();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedLocation, setSelectedLocation] = useState<string>('');
  const [animals, setAnimals] = useState<{ animalId: string; animalName: string }[]>([]);
  const [selectedAnimals, setSelectedAnimals] = useState<string[]>([]);
  const [date, setDate] = React.useState<{ start: Date | null; end: Date | null }>({ start: null, end: null });
  const [chart, setChart] = useState<{ series: any; options: ApexOptions }>({
    series: [],
    options: {
      chart: {
        id: ChartHelpers.getUuid(),
        height: 350,
        type: 'rangeBar',
        fontFamily: tokens.fontFamily,
        toolbar: {
          tools: {
            download: false
          }
        },
        animations: {
          enabled: false
        }
      },
      markers: {
        size: 0
      },
      plotOptions: {
        bar: {
          horizontal: true,
          barHeight: '50%',
          rangeBarGroupRows: true
        }
      },
      dataLabels: {
        enabled: true,
        formatter: function (val: number[]) {
          const diff = val[1] - val[0];
          const minutes = Math.floor(diff / 60000);
          const seconds = ((diff % 60000) / 1000).toFixed(0);
          return minutes + ':' + (Number(seconds) < 10 ? '0' : '') + seconds;
        },
        style: {
          colors: ['#f3f4f5', '#fff']
        }
      },
      colors: [
        // @ts-ignore
        function ({ value, seriesIndex, w }) {
          const activitiesWithColors = ChartHelpers.getActivitiesWithColors();
          return activitiesWithColors.find((activity) => activity.key === w.globals.seriesNames[seriesIndex])?.color;
        }
      ],
      fill: {
        type: 'solid'
      },
      xaxis: {
        type: 'datetime',
        labels: {
          datetimeFormatter: {
            year: 'yyyy',
            month: 'MMM yyyy',
            day: 'dd MMM',
            hour: 'HH:mm',
            minute: 'mm:ss',
            second: 'mm:ss'
          }
        }
      },
      legend: {
        show: false
      },
      tooltip: {
        y: {
          formatter(val: any, _opts?: any): string {
            return new Date(val).toLocaleString();
          }
        }
      }
    }
  });

  const setStartDate = (value: unknown) => {
    const startDate = value as Date | null;
    setDate({ ...date, start: startDate });
  };

  const setEndDate = (value: unknown) => {
    const endDate = value as Date | null;
    setDate({ ...date, end: endDate });
  };

  const handleLocationChange = (event: any) => {
    setSelectedLocation(event.target.value);

    locationsService.getLocationAnimals(event.target.value).then((res) => {
      setAnimals(
        res.animals.map((animal) => {
          return {
            animalId: animal.id,
            animalName: animal.number
          };
        })
      );
      setSelectedAnimals(
        res.animals.map((animal) => {
          return animal.id;
        })
      );
    });
  };

  const handleAnimalChange = (event: any) => {
    const value = event.target.value;
    setSelectedAnimals(value);
  };
  const isDisabled = !selectedLocation || animals.length === 0 || !date.start || !date.end;

  const getStatistics = () => {
    // validate + message
    if (!date.start || !date.end) return;
    setIsLoading(true);
    const graphData: GraphData[] = [];

    vitalityTrackerService
      .getDetailedGroupAnalytics(selectedAnimals, date.start, date.end)
      .then((result) => {
        const flatResult = result.data.flat() as AnimalGroupAnalyticDetails[];

        for (const animal of flatResult) {
          for (const data of animal.data.slice(0, 300)) {
            const graphSeries: GraphSeries = {
              x: animal.number,
              y: [new Date(data.from).getTime(), new Date(data.to).getTime()]
            };
            const graphDataItem = graphData.find((item) => item.name === FloorPlanAreaType[data.type]);
            if (graphDataItem) {
              graphDataItem.data.push(graphSeries);
            } else {
              graphData.push({ name: FloorPlanAreaType[data.type], data: [graphSeries] });
            }
          }
        }
        setChart({ ...chart, series: graphData });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    activities.forEach((activity) => {
      const id = chart.options.chart?.id;
      if (id) {
        // Check if activity is in chart
        const exists = chart.series.find((item: any) => item.name === activity.key);
        if (exists) ApexCharts.exec(id, activity.isSelected ? 'showSeries' : 'hideSeries', activity.key);
      }
    });
  }, [activities]);

  useEffect(() => {
    setChart({
      series: chart.series,
      options: {
        ...chart.options,
        noData: {
          ...chart.options.noData,
          text: isLoading ? 'Loading...' : undefined
        }
      }
    });
  }, [isLoading]);

  return (
    <Grid container spacing={2}>
      <Grid item md={3}>
        <FormControl fullWidth variant="outlined" size="small" margin="normal" disabled={isLoading}>
          <InputLabel id="selectRoomLabel">Select Room</InputLabel>
          <Select
            labelId="selectRoomLabel"
            id="selectRoomSelect"
            label="Select Room"
            value={selectedLocation}
            onChange={handleLocationChange}
          >
            {locations.map((location) => {
              return (
                <MenuItem key={location.id} value={location.id}>
                  {location.name}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
        <FormControl fullWidth variant="outlined" size="small" margin="normal" disabled={isLoading}>
          <InputLabel id="selectAnimalsLabel">Select Animals</InputLabel>
          <Select
            multiple
            disabled={animals.length === 0}
            labelId="selectAnimalsLabel"
            id="selectAnimalsSelect"
            label="Select Animals"
            value={selectedAnimals}
            onChange={handleAnimalChange}
            renderValue={(selected) => `${selected.length} selected`}
          >
            {animals.map((animal) => {
              return (
                <MenuItem key={animal.animalId} value={animal.animalId}>
                  <Checkbox checked={selectedAnimals.includes(animal.animalId)} color="secondary" />
                  <ListItemText primary={animal.animalName} />
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
        <FormControl fullWidth disabled={isLoading} variant="standard">
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DesktopDatePicker
              closeOnSelect
              format="dd/MM/yyyy"
              value={date.start}
              onChange={setStartDate}
              label="From"
              slotProps={{
                textField: {
                  variant: 'outlined',
                  size: 'small',
                  margin: 'normal',
                  id: 'selectFromLabel',
                  'aria-label': 'change date'
                }
              }}
            />
          </LocalizationProvider>
        </FormControl>
        <FormControl fullWidth disabled={isLoading} variant="standard">
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DesktopDatePicker
              closeOnSelect
              format="dd/MM/yyyy"
              value={date.end}
              onChange={setEndDate}
              label="To"
              slotProps={{
                textField: {
                  variant: 'outlined',
                  size: 'small',
                  margin: 'normal',
                  id: 'selectToLabel',
                  'aria-label': 'change date'
                }
              }}
            />
          </LocalizationProvider>
        </FormControl>

        <Button
          fullWidth
          variant="contained"
          color="primary"
          size="medium"
          onClick={getStatistics}
          disabled={isDisabled || isLoading}
        >
          Load statistics
        </Button>
      </Grid>
      <Grid item md={9}>
        <div id="chart">
          {chart.series.length > 0 && (
            /*
                        // @ts-ignore */
            <ReactApexChart options={chart.options} series={chart.series} type="rangeBar" height={350}></ReactApexChart>
          )}
        </div>
      </Grid>
    </Grid>
  );
};

export default Detailed;
